import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  aggregate,
  detectRelations,
  computeCalculatedField,
  groupBy,
  type Aggregation,
  type BiRelation,
  type BiSourceMeta,
  type ColumnMeta,
  type Row,
} from '@/lib/biData';
import { exportBiCsv, exportBiExcel, exportBiPdf, exportChartImage } from '@/lib/biExport';
import ChartCard from './ChartCard';

// ============================================================
// Zifek BI — deux composants :
//  • DashboardBuilder : composition visuelle drag & drop de widgets
//    (déplacer / redimensionner / supprimer), positions sauvegardées
//    dans la table bi_dashboards.
//  • BiModelPanel : relations entre fichiers (colonnes communes) et
//    création de champs calculés.
// ============================================================

const ROW_H = 72;
const GRID_COLS = 12;

interface Widget {
  id: string;
  type: 'chart' | 'kpi';
  title: string;
  chartType: 'bar' | 'line' | 'pie';
  dimension: string;
  measure: string;
  agg: Aggregation;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface DashboardRecord {
  id: number;
  name: string;
  config: { widgets?: Widget[] } | null;
}

interface BuilderProps {
  columns: ColumnMeta[];
  rows: Row[];
  datasetId: number | null;
  datasetName: string;
  commerceId: number | null;
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function DashboardBuilder({ columns, rows, datasetId, datasetName, commerceId }: BuilderProps) {
  const numericColumns = useMemo(() => columns.filter((c) => c.type === 'number').map((c) => c.name), [columns]);
  const dimensionColumns = useMemo(
    () => columns.filter((c) => (c.type === 'string' || c.type === 'date' || c.type === 'boolean')).map((c) => c.name),
    [columns],
  );

  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [dashboards, setDashboards] = useState<DashboardRecord[]>([]);
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [name, setName] = useState(datasetName || 'Mon dashboard');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ id: string; mode: 'move' | 'resize'; startX: number; startY: number; ox: number; oy: number; ow: number; oh: number } | null>(null);

  const loadDashboards = useCallback(async () => {
    if (!commerceId) return;
    const { data } = await supabase
      .from('bi_dashboards')
      .select('id, name, config')
      .eq('idcommerce', commerceId)
      .order('updated_at', { ascending: false });
    setDashboards((data as DashboardRecord[]) || []);
  }, [commerceId]);

  useEffect(() => { void loadDashboards(); }, [loadDashboards]);

  const openDashboard = (d: DashboardRecord) => {
    setCurrentId(d.id);
    setName(d.name);
    setWidgets(d.config?.widgets || []);
  };

  const addChartWidget = () => {
    setWidgets((prev) => [
      ...prev,
      {
        id: uid(), type: 'chart', title: 'Nouveau graphique',
        chartType: 'bar', dimension: dimensionColumns[0] || '', measure: numericColumns[0] || '',
        agg: 'SUM', x: 0, y: prev.length * 4, w: 6, h: 4,
      },
    ]);
  };

  const addKpiWidget = () => {
    setWidgets((prev) => [
      ...prev,
      {
        id: uid(), type: 'kpi', title: 'KPI', chartType: 'bar',
        dimension: '', measure: numericColumns[0] || '', agg: 'SUM', x: 0, y: prev.length * 4, w: 3, h: 2,
      },
    ]);
  };

  const updateWidget = (id: string, patch: Partial<Widget>) => {
    setWidgets((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  };
  const removeWidget = (id: string) => setWidgets((prev) => prev.filter((w) => w.id !== id));

  const onPointerDown = (e: React.PointerEvent, w: Widget, mode: 'move' | 'resize') => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { id: w.id, mode, startX: e.clientX, startY: e.clientY, ox: w.x, oy: w.y, ow: w.w, oh: w.h };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const colW = rect.width / GRID_COLS;
    const dx = Math.round((e.clientX - d.startX) / colW);
    const dy = Math.round((e.clientY - d.startY) / ROW_H);
    if (d.mode === 'move') {
      updateWidget(d.id, {
        x: Math.max(0, Math.min(GRID_COLS - 1, d.ox + dx)),
        y: Math.max(0, d.oy + dy),
      });
    } else {
      updateWidget(d.id, {
        w: Math.max(2, Math.min(GRID_COLS, d.ow + dx)),
        h: Math.max(2, d.oh + dy),
      });
    }
  };

  const endDrag = () => { dragRef.current = null; };

  const saveDashboard = async () => {
    if (!commerceId) { setMsg('Marchand introuvable.'); return; }
    setSaving(true);
    setMsg('');
    try {
      const payload = { idcommerce: commerceId, name: name.trim() || 'Mon dashboard', dataset_id: datasetId, config: { widgets } };
      if (currentId) {
        const { error } = await supabase.from('bi_dashboards').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', currentId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('bi_dashboards').insert(payload).select('id').single();
        if (error) throw error;
        setCurrentId(data?.id as number);
      }
      await loadDashboards();
      setMsg('Dashboard enregistré ✓');
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur d\u2019enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Barre d'outils */}
      <div className="flex flex-wrap items-center gap-2 bg-background-50 border border-background-200/70 rounded-xl p-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:border-primary-300 min-w-[200px]"
          placeholder="Nom du dashboard"
        />
        <select
          value={currentId ?? ''}
          onChange={(e) => {
            const id = e.target.value ? Number(e.target.value) : null;
            setCurrentId(id);
            const d = dashboards.find((x) => x.id === id);
            if (d) openDashboard(d);
          }}
          className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm cursor-pointer"
        >
          <option value="">— Nouveau dashboard —</option>
          {dashboards.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button onClick={addChartWidget} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 cursor-pointer whitespace-nowrap">
          <i className="ri-bar-chart-2-line"></i>Graphique
        </button>
        <button onClick={addKpiWidget} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 cursor-pointer whitespace-nowrap">
          <i className="ri-number-2"></i>KPI
        </button>
        <button onClick={saveDashboard} disabled={saving} className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 cursor-pointer whitespace-nowrap">
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}Enregistrer
        </button>
        <button onClick={() => exportBiExcel(name || 'dashboard', columns, rows)} className="w-9 h-9 rounded-full border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer" title="Exporter Excel">
          <i className="ri-file-excel-2-line"></i>
        </button>
        <button onClick={() => exportBiCsv(name || 'dashboard', columns, rows)} className="w-9 h-9 rounded-full border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer" title="Exporter CSV">
          <i className="ri-file-text-line"></i>
        </button>
        <button
          onClick={() => exportBiPdf(name || 'dashboard', columns, rows, {
            kpis: numericColumns.slice(0, 3).map((c) => ({ label: `Total ${c}`, value: aggregate(rows, c, 'SUM').toLocaleString('fr-FR', { maximumFractionDigits: 2 }) })),
          })}
          className="w-9 h-9 rounded-full border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer" title="Exporter PDF"
        >
          <i className="ri-file-pdf-2-line"></i>
        </button>
      </div>
      {msg && <p className="text-xs text-foreground-500">{msg}</p>}

      {/* Canvas */}
      <div
        ref={canvasRef}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        className="relative bg-background-100/40 border border-background-200/70 rounded-xl"
        style={{ height: widgets.reduce((m, w) => Math.max(m, w.y + w.h), 0) * ROW_H + 24, minHeight: 200 }}
      >
        {widgets.length === 0 && (
          <p className="absolute inset-0 flex items-center justify-center text-sm text-foreground-400">Ajoutez un graphique ou un KPI pour composer votre dashboard.</p>
        )}
        {widgets.map((w) => {
          const left = (w.x / GRID_COLS) * 100;
          const width = (w.w / GRID_COLS) * 100;
          return (
            <div
              key={w.id}
              className="absolute group"
              style={{ left: `${left}%`, top: w.y * ROW_H + 12, width: `${width}%`, height: w.h * ROW_H - 8, padding: '0 6px' }}
            >
              <div className="relative h-full bg-background-50 border border-background-200/70 rounded-xl overflow-hidden flex flex-col">
                <div
                  onPointerDown={(e) => onPointerDown(e, w, 'move')}
                  className="flex items-center gap-2 px-3 py-1.5 bg-background-100/70 cursor-move select-none border-b border-background-200/70"
                >
                  <i className="ri-drag-move-2-line text-foreground-400"></i>
                  <input
                    value={w.title}
                    onChange={(e) => updateWidget(w.id, { title: e.target.value })}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="flex-1 bg-transparent text-xs font-semibold text-foreground-800 focus:outline-none min-w-0"
                  />
                  <button onClick={() => removeWidget(w.id)} className="w-6 h-6 flex items-center justify-center rounded text-foreground-400 hover:text-red-500 cursor-pointer" title="Supprimer">
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </div>

                {w.type === 'kpi' ? (
                  <div className="flex-1 flex flex-col items-center justify-center px-3">
                    <p className="text-2xl font-bold text-foreground-950">
                      {aggregate(rows, w.measure, w.agg).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
                    </p>
                    <div className="flex items-center gap-1 mt-2 text-[11px]">
                      <select value={w.measure} onChange={(e) => updateWidget(w.id, { measure: e.target.value })} className="bg-background-100 rounded px-1 py-0.5 cursor-pointer max-w-[110px]">
                        {numericColumns.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select value={w.agg} onChange={(e) => updateWidget(w.id, { agg: e.target.value as Aggregation })} className="bg-background-100 rounded px-1 py-0.5 cursor-pointer">
                        {(['SUM', 'AVG', 'COUNT', 'MIN', 'MAX', 'DISTINCT'] as Aggregation[]).map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex flex-wrap items-center gap-1 px-2 py-1 text-[11px]">
                      <select value={w.dimension} onChange={(e) => updateWidget(w.id, { dimension: e.target.value })} className="bg-background-100 rounded px-1 py-0.5 cursor-pointer max-w-[110px]">
                        {dimensionColumns.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select value={w.measure} onChange={(e) => updateWidget(w.id, { measure: e.target.value })} className="bg-background-100 rounded px-1 py-0.5 cursor-pointer max-w-[110px]">
                        {numericColumns.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select value={w.agg} onChange={(e) => updateWidget(w.id, { agg: e.target.value as Aggregation })} className="bg-background-100 rounded px-1 py-0.5 cursor-pointer">
                        {(['SUM', 'AVG', 'COUNT', 'MIN', 'MAX', 'DISTINCT'] as Aggregation[]).map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <select value={w.chartType} onChange={(e) => updateWidget(w.id, { chartType: e.target.value as 'bar' | 'line' | 'pie' })} className="bg-background-100 rounded px-1 py-0.5 cursor-pointer">
                        <option value="bar">Barres</option><option value="line">Ligne</option><option value="pie">Camembert</option>
                      </select>
                      <button
                        onClick={(e) => {
                          const svg = (e.currentTarget.closest('.group') as HTMLElement)?.querySelector('svg') as SVGSVGElement | null;
                          void exportChartImage(w.title, svg);
                        }}
                        className="ml-auto w-5 h-5 flex items-center justify-center rounded text-foreground-400 hover:text-primary-600 cursor-pointer"
                        title="Exporter en image"
                      >
                        <i className="ri-image-line text-xs"></i>
                      </button>
                    </div>
                    <div className="flex-1 min-h-0 px-1 pb-1">
                      {w.dimension && w.measure ? (
                        <ChartCard
                          title=""
                          chartType={w.chartType}
                          points={groupBy(rows, w.dimension, w.measure, w.agg).slice(0, 20)}
                          height={Math.max(120, (w.h * ROW_H) - 120)}
                        />
                      ) : (
                        <p className="text-xs text-foreground-400 p-3">Choisissez une dimension et une mesure.</p>
                      )}
                    </div>
                  </div>
                )}

                <div
                  onPointerDown={(e) => onPointerDown(e, w, 'resize')}
                  className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize opacity-0 group-hover:opacity-100"
                >
                  <span className="absolute bottom-1 right-1 w-2.5 h-2.5 border-r-2 border-b-2 border-foreground-400 rounded-br-sm"></span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Relations entre fichiers + champs calculés
// ────────────────────────────────────────────────────────────

export function BiModelPanel({
  sources,
  columns,
  rows,
  onApplyField,
}: {
  sources: BiSourceMeta[];
  columns: ColumnMeta[];
  rows: Row[];
  onApplyField: (name: string, rows: Row[], column: ColumnMeta) => void;
}) {
  const relations: BiRelation[] = useMemo(() => detectRelations(sources), [sources]);
  const [fieldName, setFieldName] = useState('');
  const [formula, setFormula] = useState('');
  const [error, setError] = useState('');
  const numeric = columns.filter((c) => c.type === 'number').map((c) => c.name);

  const createField = () => {
    setError('');
    if (!fieldName.trim() || !formula.trim()) { setError('Renseignez un nom et une formule.'); return; }
    const res = computeCalculatedField(rows, columns.map((c) => c.name), fieldName, formula);
    if (!res) { setError('Formule invalide. Utilisez uniquement des noms de colonnes, chiffres et + - * / ( ).'); return; }
    onApplyField(fieldName.trim(), res.rows, res.column);
    setFieldName(''); setFormula('');
  };

  return (
    <div className="space-y-5">
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
          <i className="ri-links-line text-primary-500"></i>Relations entre fichiers
        </h3>
        {relations.length === 0 ? (
          <p className="text-xs text-foreground-400">Aucune colonne commune détectée entre vos sources. Importez plusieurs fichiers partageant une colonne (ex : identifiant client).</p>
        ) : (
          <div className="space-y-2">
            {relations.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs bg-background-100/60 rounded-lg px-3 py-2">
                <span className="font-medium text-foreground-800">{r.sourceName}</span>
                <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">{r.sourceColumn}</span>
                <i className="ri-arrow-left-right-line text-foreground-400"></i>
                <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">{r.targetColumn}</span>
                <span className="font-medium text-foreground-800">{r.targetName}</span>
                <span className="ml-auto text-foreground-400">{r.type}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-1 flex items-center gap-2">
          <i className="ri-function-line text-accent-500"></i>Champs calculés
        </h3>
        <p className="text-xs text-foreground-500 mb-3">Créez une nouvelle colonne à partir d&apos;une formule. Colonnes numériques disponibles : {numeric.join(', ') || 'aucune'}.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input value={fieldName} onChange={(e) => setFieldName(e.target.value)} placeholder="Nom du champ (ex : Marge)" className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:border-primary-300" />
          <input value={formula} onChange={(e) => setFormula(e.target.value)} placeholder="Formule (ex : Ventes - Cout, ou Ventes / 100)" className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm font-mono focus:outline-none focus:border-primary-300" />
        </div>
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        <button onClick={createField} className="mt-3 px-4 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer">
          Créer le champ
        </button>
      </div>
    </div>
  );
}